/**
 * XP System - Minecraft-style experience points
 *
 * - Gain XP from mining, killing mobs, smelting
 * - XP accumulates and causes level ups
 * - Levels are spent on enchanting
 * - On death, all XP is lost
 */
export class XPSystem {
  constructor() {
    this.level = 0;
    this.xp = 0;           // XP points in current level
    this.totalXp = 0;      // Total XP ever gained
  }

  /**
   * XP needed to complete the given level (simplified Minecraft formula).
   * Level 0 needs 7, level 1 needs 10, level 2 needs 13, etc.
   */
  xpForLevel(level) {
    return 7 + level * 3;
  }

  /**
   * Progress toward next level as a fraction 0..1
   */
  getProgress() {
    const needed = this.xpForLevel(this.level);
    if (needed <= 0) return 0;
    return Math.min(1, this.xp / needed);
  }

  /**
   * Add XP points. Handles multiple level-ups in one call.
   * Returns the number of levels gained.
   */
  addXP(amount) {
    if (amount <= 0) return 0;
    this.xp += amount;
    this.totalXp += amount;

    let levelsGained = 0;
    while (this.xp >= this.xpForLevel(this.level)) {
      this.xp -= this.xpForLevel(this.level);
      this.level++;
      levelsGained++;
    }
    return levelsGained;
  }

  /**
   * Spend XP levels (used for enchanting).
   * Returns true if the player had enough levels and they were deducted.
   */
  spendLevels(levels) {
    if (this.level < levels) return false;
    this.level -= levels;
    return true;
  }

  /**
   * Called on death -- lose all XP and levels.
   */
  onDeath() {
    this.level = 0;
    this.xp = 0;
    // totalXp is kept as a lifetime stat
  }

  /**
   * Serialize for save/load
   */
  serialize() {
    return {
      level: this.level,
      xp: this.xp,
      totalXp: this.totalXp,
    };
  }

  /**
   * Deserialize from saved data
   */
  deserialize(data) {
    if (!data) return;
    this.level = data.level || 0;
    this.xp = data.xp || 0;
    this.totalXp = data.totalXp || 0;
  }
}
